import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CTAButtonLight } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.1,
      ease: 'easeOut'
    }
  })
};
export function RecruitingCostCalculator() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.15
  });
  const [hourlyRate, setHourlyRate] = useState(25);
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [adSpend, setAdSpend] = useState(500);
  const [hiresPerMonth, setHiresPerMonth] = useState(3);
  const weeksPerMonth = 4.33;
  const laborCost = Math.round(hourlyRate * hoursPerWeek * weeksPerMonth);
  const totalCost = laborCost + adSpend;
  const costPerHire = hiresPerMonth > 0 ? Math.round(totalCost / hiresPerMonth) : 0;
  const isExpensive = costPerHire > 350;
  const overpay = costPerHire - 350;
  const sliders = [{
    label: 'Recruiter rate',
    value: hourlyRate,
    min: 15,
    max: 100,
    step: 5,
    display: `$${hourlyRate}/hr`,
    onChange: setHourlyRate
  }, {
    label: 'Hours/week recruiting',
    value: hoursPerWeek,
    min: 1,
    max: 40,
    step: 1,
    display: `${hoursPerWeek} hrs`,
    onChange: setHoursPerWeek
  }, {
    label: 'Monthly ad spend',
    value: adSpend,
    min: 0,
    max: 5000,
    step: 100,
    display: `$${adSpend.toLocaleString()}`,
    onChange: setAdSpend
  }, {
    label: 'Hires per month',
    value: hiresPerMonth,
    min: 1,
    max: 30,
    step: 1,
    display: `${hiresPerMonth}`,
    onChange: setHiresPerMonth
  }];
  return <section className="w-full bg-[#1A2B4A] py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden" data-id="element-491">
      <div className="absolute inset-0 opacity-20" style={{
      backgroundImage: 'linear-gradient(rgba(78,205,196,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,0.08) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-492" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-[#4ECDC4]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-493" />

      <div ref={ref} className="max-w-lg mx-auto relative z-10" data-id="element-494">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-6 sm:mb-10" data-id="element-495">
          <span className="inline-block bg-[#E8655A]/20 text-[#E8655A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-3 tracking-wider border border-[#E8655A]/30" data-id="element-496">
            Cost Calculator
          </span>
          <h2 className="text-2xl sm:text-4xl font-black text-white mb-2 leading-tight" data-id="element-497">
            What Is Recruiting
            <br data-id="element-498" />
            <span className="text-[#4ECDC4]" data-id="element-499">Actually Costing You?</span>
          </h2>
        </motion.div>

        {/* Compact sliders + inline breakdown */}
        <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="rounded-2xl p-4 sm:p-6 mb-4 space-y-4" style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)'
      }} data-id="element-500">
          {sliders.map(s => {
          const pct = (s.value - s.min) / (s.max - s.min) * 100;
          return <div key={s.label} data-id="element-501">
                <div className="flex justify-between items-baseline mb-1" data-id="element-502">
                  <label className="text-white/70 text-xs sm:text-sm font-medium" data-id="element-503">
                    {s.label}
                  </label>
                  <span className="text-[#4ECDC4] font-black text-sm" data-id="element-504">
                    {s.display}
                  </span>
                </div>
                <input type="range" min={s.min} max={s.max} step={s.step} value={s.value} onChange={e => s.onChange(Number(e.target.value))} className="w-full h-1.5 rounded-full appearance-none cursor-pointer" style={{
              accentColor: '#4ECDC4',
              background: `linear-gradient(to right, #4ECDC4 ${pct}%, rgba(255,255,255,0.12) ${pct}%)`
            }} data-id="element-505" />
              </div>;
        })}

          {/* Inline breakdown */}
          <div className="pt-3 border-t border-white/10 space-y-1.5" data-id="element-506">
            <div className="flex justify-between text-xs" data-id="element-507">
              <span className="text-white/50" data-id="element-508">Labor</span>
              <span className="text-white/80 font-semibold" data-id="element-509">
                ${laborCost.toLocaleString()}/mo
              </span>
            </div>
            <div className="flex justify-between text-xs" data-id="element-510">
              <span className="text-white/50" data-id="element-511">Ad spend</span>
              <span className="text-white/80 font-semibold" data-id="element-512">
                ${adSpend.toLocaleString()}/mo
              </span>
            </div>
            <div className="flex justify-between text-xs pt-1.5 border-t border-white/8" data-id="element-513">
              <span className="text-white font-bold" data-id="element-514">Total</span>
              <span className="text-white font-black" data-id="element-515">
                ${totalCost.toLocaleString()}/mo
              </span>
            </div>
          </div>
        </motion.div>

        {/* Result — always visible */}
        <motion.div custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="rounded-2xl p-5 sm:p-8 text-center mb-5" style={{
        background: isExpensive ? 'linear-gradient(135deg, rgba(232,101,90,0.12), rgba(232,101,90,0.06))' : 'linear-gradient(135deg, rgba(78,205,196,0.12), rgba(78,205,196,0.06))',
        border: `1px solid ${isExpensive ? 'rgba(232,101,90,0.25)' : 'rgba(78,205,196,0.25)'}`
      }} data-id="element-516">
          <p className="text-white/50 text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-1" data-id="element-517">
            Your True Cost Per Hire
          </p>
          <p className={`text-5xl sm:text-7xl font-black mb-2 ${isExpensive ? 'text-[#E8655A]' : 'text-[#4ECDC4]'}`} data-id="element-518">
            ${costPerHire.toLocaleString()}
          </p>
          {isExpensive ? <>
              <p className="text-white/90 text-sm sm:text-base font-semibold mb-1" data-id="element-519">
                You're paying{' '}
                <span className="text-[#E8655A] font-black" data-id="element-520">
                  ${overpay.toLocaleString()} too much
                </span>{' '}
                per hire.
              </p>
              <p className="text-white/55 text-xs sm:text-sm max-w-sm mx-auto mb-4" data-id="element-521">
                Our clients average{' '}
                <span className="text-white font-semibold" data-id="element-522">$350 or less</span>{' '}
                per qualified hire — fully managed, triple-screened, and ready
                to close.
              </p>
              <CTAButtonLight text="See what a low cost per hire looks like →" data-id="element-523" />
            </> : <>
              <p className="text-white/80 text-sm sm:text-base font-semibold mb-1" data-id="element-524">
                You're near the benchmark — but are your hires{' '}
                <span className="text-[#4ECDC4]" data-id="element-525">actually showing up?</span>
              </p>
              <p className="text-white/50 text-xs sm:text-sm max-w-sm mx-auto mb-4" data-id="element-526">
                Cost is only half the equation. Our system delivers
                triple-screened reps who stay and sell.
              </p>
              <CTAButtonLight text="See what a low cost per hire looks like →" data-id="element-527" />
            </>}
        </motion.div>
      </div>
    </section>;
}